import type {
  ChannelThreadId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  Message,
  MessageWithSupportTicketId,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import type { SupportTicketTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { encodeAdvancedFilterGroup } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { and, eq, inArray, useLiveQuery } from "@tanstack/react-db";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type { ConversationPresenceRecord } from "../db/collections";
import {
  fetchSingleSupportTicket,
  PREFETCH_PAGES_AHEAD,
} from "../db/support-ticket-list-collection";
import { useQueryOptions } from "../filters/filters-context";
import { filterGroupToTanstackExpression } from "../filters/support-ticket-tanstack-expressions";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

const SUPPORT_TICKETS_PAGE_QUERY_KEY = "support-tickets-page";
const SUPPORT_TICKET_QUERY_KEY = "support-ticket";
const STALE_TIME = 5 * 60 * 1000;

interface PageMetadata {
  supportTicketIds: string[];
  totalCount: number;
  page: number;
}

export function useSupportTickets(page: number): {
  data: SupportTicketListItemResponse[];
  totalCount: number;
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const queryClient = useQueryClient();
  const queryOptions = useQueryOptions();
  const filterGroup = queryOptions.filterGroup;
  const encodedFilterGroup = filterGroup
    ? encodeAdvancedFilterGroup(filterGroup)
    : undefined;

  const { data: pageMetadata, isLoading: isFetching } = useQuery({
    queryKey: [SUPPORT_TICKETS_PAGE_QUERY_KEY, page, encodedFilterGroup],
    queryFn: async (): Promise<PageMetadata> => {
      const result = await collections.supportTicketsList.fetchPage(
        page,
        encodedFilterGroup,
      );
      return {
        supportTicketIds: result.items.map((item) => item.id),
        totalCount: result.totalCount,
        page: result.page,
      };
    },
    staleTime: STALE_TIME,
  });

  const supportTicketIds = useMemo(
    () => pageMetadata?.supportTicketIds ?? [],
    [pageMetadata?.supportTicketIds],
  );
  const idsKey = supportTicketIds.join(",");

  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ supportTicket: collections.supportTicketsList.collection })
        .where(({ supportTicket }) => {
          const idFilter = inArray(supportTicket.id, supportTicketIds);
          const filterExpr = filterGroup
            ? filterGroupToTanstackExpression(filterGroup, supportTicket)
            : null;
          return filterExpr ? and(idFilter, filterExpr) : idFilter;
        })
        .orderBy(({ supportTicket }) => supportTicket.lastCustomerMessageAt, {
          direction: "desc",
          nulls: "last",
        }),
    [idsKey, encodedFilterGroup, collections.supportTicketsList.collection],
  );

  useMemo(() => {
    for (let i = 1; i <= PREFETCH_PAGES_AHEAD; i++) {
      const pageToLoad = page + i;
      void queryClient.prefetchQuery({
        queryKey: [
          SUPPORT_TICKETS_PAGE_QUERY_KEY,
          pageToLoad,
          encodedFilterGroup,
        ],
        queryFn: async (): Promise<PageMetadata> => {
          const result = await collections.supportTicketsList.fetchPage(
            pageToLoad,
            encodedFilterGroup,
          );
          return {
            supportTicketIds: result.items.map((item) => item.id),
            totalCount: result.totalCount,
            page: result.page,
          };
        },
        staleTime: STALE_TIME,
      });
    }
  }, [page, encodedFilterGroup, queryClient, collections.supportTicketsList]);

  return {
    data: liveResult.data,
    totalCount: pageMetadata?.totalCount ?? 0,
    isLoading: isFetching || liveResult.isLoading,
  };
}

export function useSupportTicket(supportTicketId: SupportTicketId): {
  data: SupportTicketListItemResponse | undefined;
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();
  const effectApi = useRequiredContext(EffectApiClientContext);

  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ supportTicket: collections.supportTicketsList.collection })
        .where(({ supportTicket }) => eq(supportTicket.id, supportTicketId))
        .findOne(),
    [supportTicketId, collections.supportTicketsList.collection],
  );

  const { isLoading: isFetchingSingle } = useQuery({
    queryKey: [SUPPORT_TICKET_QUERY_KEY, supportTicketId],
    queryFn: async () => {
      const supportTicket = await fetchSingleSupportTicket(
        effectApi,
        supportTicketId,
      );
      collections.supportTicketsList.collection.utils.writeUpsert(
        supportTicket,
      );
      return supportTicket;
    },
    enabled: liveResult.data === undefined,
    staleTime: STALE_TIME,
  });

  return {
    data: liveResult.data,
    isLoading: liveResult.isLoading || isFetchingSingle,
  };
}

export function useChannelThreads(supportTicketId: SupportTicketId): {
  data: ChannelThread[];
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();

  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ thread: collections.threads })
        .where(({ thread }) => eq(thread.supportTicketId, supportTicketId))
        .orderBy(({ thread }) => thread.createdAt, "asc"),
    [supportTicketId],
  );

  return { data: liveResult.data, isLoading: liveResult.isLoading };
}

export function useSupportTicketMessages(supportTicketId: SupportTicketId): {
  data: MessageWithSupportTicketId[];
  isLoading: boolean;
} {
  const collections = useGlobalSupportTicketCollections();

  const liveResult = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.supportTicketId, supportTicketId))
        .orderBy(({ message }) => message.sentAt, "asc"),
    [supportTicketId],
  );

  return { data: liveResult.data, isLoading: liveResult.isLoading };
}

export function useThreadMessages(
  supportTicketId: SupportTicketId,
  threadIds: ChannelThreadId | readonly ChannelThreadId[],
): { data: Message[]; isLoading: boolean } {
  const collections = useGlobalSupportTicketCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ message: collections.messages })
        .where(({ message }) => eq(message.supportTicketId, supportTicketId))
        .orderBy(({ message }) => message.sentAt, "asc"),
    [supportTicketId],
  );

  const threadIdSet = useMemo(
    () => new Set(Array.isArray(threadIds) ? threadIds : [threadIds]),
    [threadIds],
  );

  const data = useMemo(
    () => result.data.filter((m) => threadIdSet.has(m.channelThreadId)),
    [result.data, threadIdSet],
  );

  return { data, isLoading: result.isLoading };
}

export function useSupportTicketTimelineEvents(
  supportTicketId: SupportTicketId,
): { data: SupportTicketTimelineEventResponse[]; isLoading: boolean } {
  const collections = useGlobalSupportTicketCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ event: collections.timelineEvents })
        .where(({ event }) => eq(event.supportTicketId, supportTicketId))
        .orderBy(({ event }) => event.occurredAt, "desc"),
    [supportTicketId],
  );

  return { data: result.data ?? [], isLoading: result.isLoading };
}

export function useSupportTicketPresence(supportTicketId: SupportTicketId): {
  data: ConversationPresenceRecord[];
} {
  const collections = useGlobalSupportTicketCollections();

  const result = useLiveQuery(
    (q) =>
      q
        .from({ presence: collections.presence })
        .where(({ presence }) => eq(presence.supportTicketId, supportTicketId)),
    [supportTicketId],
  );

  return { data: result.data };
}

export function useSingleSupportTicketData(supportTicketId: SupportTicketId): {
  conversation: SupportTicketListItemResponse | undefined;
  threads: ChannelThread[];
  messages: Message[];
  isLoading: boolean;
} {
  const conversationResult = useSupportTicket(supportTicketId);
  const threadsResult = useChannelThreads(supportTicketId);
  const messagesResult = useSupportTicketMessages(supportTicketId);

  return {
    conversation: conversationResult.data,
    threads: threadsResult.data,
    messages: messagesResult.data,
    isLoading:
      threadsResult.isLoading ||
      messagesResult.isLoading ||
      conversationResult.isLoading,
  };
}
